import React, { useEffect, useRef } from 'react';

// Excel-style range copy for a <table> (Judy, 2026-11-04): drag (or shift-click) across cells, then
// Ctrl+C pastes as tab-separated rows. A cell with data-v copies that exact value instead of its
// rounded display text, so pasted dollars aren't the $K figures on screen.
const SEL = 'cell-sel';
const colOf = (td) => { let c = 0; for (let x = td.previousElementSibling; x; x = x.previousElementSibling) c += x.colSpan || 1; return c; };
const cellAt = (row, col) => { let c = 0; for (const td of row.cells) { if (col < c + (td.colSpan || 1)) return td; c += td.colSpan || 1; } return null; };
const textOf = (td) => (td.dataset.v != null ? td.dataset.v : td.textContent.replace(/[▸▾▲▼]/g, '').replace(/\s+/g, ' ').trim());

// Rounds to cents and drops '$'/commas so it pastes as a number; '' for null.
export const exactV = (n) => (n == null || !Number.isFinite(n) ? '' : String(Math.round(n * 100) / 100));

export function CopyTable({ children, className, ...rest }) {
  const ref = useRef(null);
  const s = useRef({ a: null, b: null, drag: false, moved: false });

  const grid = () => {
    const { a, b } = s.current;
    if (!a || !b || !ref.current) return [];
    const out = [];
    for (let r = Math.min(a.r, b.r); r <= Math.max(a.r, b.r); r += 1) {
      const row = ref.current.rows[r];
      if (!row) continue;
      const line = [];
      for (let c = Math.min(a.c, b.c); c <= Math.max(a.c, b.c); c += 1) {
        const td = cellAt(row, c);
        if (td && line[line.length - 1] !== td) line.push(td);
      }
      out.push(line);
    }
    return out;
  };
  const paint = () => {
    if (!ref.current) return;
    ref.current.querySelectorAll(`.${SEL}`).forEach((e) => e.classList.remove(SEL));
    grid().flat().forEach((td) => td.classList.add(SEL));
  };
  const clear = () => { s.current.a = null; s.current.b = null; paint(); };
  const posOf = (e) => {
    const td = e.target.closest?.('td,th');
    return td && ref.current?.contains(td) ? { r: td.parentElement.rowIndex, c: colOf(td) } : null;
  };

  useEffect(() => {
    const up = () => { s.current.drag = false; };
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) clear(); };
    const key = (e) => {
      if (!s.current.a) return;
      if (e.key === 'Escape') { clear(); return; }
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'c') return;
      if (!window.getSelection()?.isCollapsed) return; // real text selection wins
      const tsv = grid().map((line) => line.map(textOf).join('\t')).join('\r\n');
      e.preventDefault();
      navigator.clipboard?.writeText(tsv);
      ref.current.classList.add('cell-copied');
      setTimeout(() => ref.current?.classList.remove('cell-copied'), 400);
    };
    document.addEventListener('mouseup', up);
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mouseup', up); document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  });

  const onMouseDown = (e) => {
    if (e.button !== 0 || e.target.closest('button,select,input,a')) return;
    const p = posOf(e);
    if (!p) return;
    e.preventDefault(); // no browser text selection while dragging
    window.getSelection()?.removeAllRanges();
    if (e.shiftKey && s.current.a) s.current.b = p;
    else Object.assign(s.current, { a: p, b: p, drag: true, moved: false });
    paint();
  };
  const onMouseOver = (e) => {
    if (!s.current.drag) return;
    const p = posOf(e);
    if (!p || (p.r === s.current.b.r && p.c === s.current.b.c)) return;
    s.current.b = p;
    s.current.moved = true;
    paint();
  };
  // A drag that ends on another cell isn't a click on the row/cell underneath (drill-in, sort, expand).
  const onClickCapture = (e) => { if (s.current.moved || e.shiftKey) { e.stopPropagation(); s.current.moved = false; } };

  return (
    <table ref={ref} className={`${className || ''} copy-table`} onMouseDown={onMouseDown} onMouseOver={onMouseOver} onClickCapture={onClickCapture} {...rest}>
      {children}
    </table>
  );
}
